import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigService } from '@core/infrastructure/config/config.service';
import { getCmsConfigValue, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { CoreTimestampServerService } from '@core/infrastructure/time/server/timestamp-server.service';
import { RgpdData } from '@shared/terms-conditions/models/rgpd-data.model';

@Injectable({ providedIn: 'root' })

/**
 * Servicio para obtener información sobre RGPD
 */
export class RgpdService {
    constructor(
        private readonly configService: ConfigService,
        private readonly store: Store,
        private readonly timestampService: CoreTimestampServerService,
        private readonly translateService: TranslateService,
    ) {
        //
    }

    /**
     * Función para obtener el estado de RGPD
     */
    public isRgpdEnabled(): Observable<boolean> {
        return this.store.select(getCmsConfigValue('rgpdEnabled'));
    }

    /**
     * Función para obtener el contenido de RGPD
     */
    public getRGPDContent(layer = 'layer_1'): Observable<RgpdData> {
        return combineLatest([
            this.translateService.get([
                'dev.general.moreInfo',
                'dev.rgpd.common.title1',
                'dev.rgpd.common.title2',
                'dev.rgpd.common.title3',
                'dev.rgpd.common.title4',
                'dev.rgpd.common.title5',
                'dev.rgpd.common.content5',
                `dev.rgpd.${layer}.content1`,
                `dev.rgpd.${layer}.content2`,
                `dev.rgpd.${layer}.content3`,
                `dev.rgpd.${layer}.content4`,
            ]),
            this.store.select(getStoreAndLanguage),
        ]).pipe(
            map(([translate, storeAndLanguage]) => {
                const common = {
                    title1: translate['dev.rgpd.common.title1'],
                    title2: translate['dev.rgpd.common.title2'],
                    title3: translate['dev.rgpd.common.title3'],
                    title4: translate['dev.rgpd.common.title4'],
                    title5: translate['dev.rgpd.common.title5'],
                    content5: translate['dev.rgpd.common.content5'],
                    link: translate['dev.general.moreInfo'],
                };
                const layerObject = {
                    content1: translate[`dev.rgpd.${layer}.content1`],
                    content2: translate[`dev.rgpd.${layer}.content2`],
                    content3: translate[`dev.rgpd.${layer}.content3`],
                    content4: translate[`dev.rgpd.${layer}.content4`],
                };

                // Check if assetsUrl already contains static2 to avoid misconfiguration between Java and Node server
                const assetsUrl = this.configService.getConfigUrl<string>('assetsUrl');
                const assetsUrlWithStaticPath = assetsUrl.includes('static2') ? assetsUrl : `${assetsUrl}/static2`;

                const url = `${assetsUrlWithStaticPath}/itxwebstandard/pdf/privacy_policy/${storeAndLanguage.storeId}/privacy_policy${storeAndLanguage.languageId}`;
                const links = {
                    controller: `${url}.pdf?${this.timestampService.getTs()}#controller`,
                    use: `${url}.pdf?${this.timestampService.getTs()}#use`,
                    ground: `${url}.pdf?${this.timestampService.getTs()}#ground`,
                    rights: `${url}.pdf?${this.timestampService.getTs()}#rights`,
                    policy: `${url}.pdf?${this.timestampService.getTs()}`,
                };

                return {
                    common,
                    layer: layerObject,
                    links,
                };
            })
        );
    }
}
